import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { DocumentStatus, PublicConfig, PublicJoinResponse, RegistrationStatus } from '@ipc/shared'
import { api, isApiError } from './client'

/** Shown while /api/public/config is loading or unreachable, so the copy never shows a raw placeholder. */
export const FALLBACK_CONFIG: PublicConfig = {
  org: { name: 'the organisation', contact: 'info@synergize.dev', phone: '', controllerName: '[CONTROLLER NAME]' },
  mapTileUrl: '',
  mapAttribution: '',
  // The 5-step flow until the server says the document step is required (identity-document spec §4.1).
  document: { required: false, steps: 4 },
  reviewMediaHoldDays: 7,
}

export const keys = {
  config: ['public', 'config'] as const,
  join: (providerId: string | undefined) => ['public', 'join', providerId ?? 'default'] as const,
  recipients: ['public', 'recipients'] as const,
  places: (q: string) => ['public', 'places', q] as const,
  registration: ['member', 'registration'] as const,
  document: ['member', 'registration', 'document'] as const,
  me: ['member', 'me'] as const,
  messages: ['member', 'messages'] as const,
  signIn: ['member', 'signIn'] as const,
}

export const useConfigQuery = () =>
  useQuery({ queryKey: keys.config, queryFn: () => api('publicConfig'), staleTime: Infinity, retry: 1 })

/** `GET /api/public/join/:id` (or the default link when no id): what the start page and the face step need. */
export const useJoinInfo = (providerId: string | undefined) =>
  useQuery<PublicJoinResponse>({
    queryKey: keys.join(providerId),
    queryFn: () => (providerId ? api('publicJoin', { params: { providerId } }) : api('publicJoinDefault')),
  })

export const useRecipients = () => useQuery({ queryKey: keys.recipients, queryFn: () => api('publicRecipients') })

/** The registration in progress (cookie-based). A 401 means there is none. */
export const useRegistration = (enabled = true) =>
  useQuery<RegistrationStatus>({
    queryKey: keys.registration,
    queryFn: () => api('registrationStatus'),
    enabled,
    retry: (count, err) => !(isApiError(err) && err.status < 500) && count < 2,
    staleTime: 0,
  })

/** The identity-document step (cookie-based, Task 25): country selector, consent need, the latest try. */
export const useDocumentStatus = (enabled = true) =>
  useQuery<DocumentStatus>({
    queryKey: keys.document,
    queryFn: () => api('registrationDocument'),
    enabled,
    retry: (count, err) => !(isApiError(err) && err.status < 500) && count < 2,
    staleTime: 0,
  })

/** Fetch the registration fresh: pages navigate from this, never from local state alone. */
export function useFetchRegistration() {
  const qc = useQueryClient()
  return () => qc.fetchQuery({ queryKey: keys.registration, queryFn: () => api('registrationStatus'), staleTime: 0 })
}

export const usePlaces = (q: string) =>
  useQuery({
    queryKey: keys.places(q),
    queryFn: ({ signal }) => api('places', { query: { q }, signal }),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
  })

/* ---- Task 19: the member area (session cookie) ---- */

/** `GET /api/member/me`: the status card and the dashboard data. A 401 means nobody is logged in. */
export const useMe = (enabled = true) =>
  useQuery({
    queryKey: keys.me,
    enabled,
    queryFn: () => api('memberMe'),
    retry: (count, err) => !(isApiError(err) && err.status < 500) && count < 2,
    staleTime: 0,
  })

/** The public join info of a provider by its registry id (nothing is fetched without an id). */
export const useJoinById = (providerId: string | undefined) =>
  useQuery<PublicJoinResponse>({
    queryKey: keys.join(providerId),
    queryFn: () => api('publicJoin', { params: { providerId: providerId! } }),
    enabled: Boolean(providerId),
  })

/**
 * `GET /api/member/account/sign-in` (passwordless spec §4): how the voter signs in, whether a password is set, and
 * until when this session counts as a fresh sign-in.
 */
export const useSignInSettings = (enabled = true) =>
  useQuery({
    queryKey: keys.signIn,
    enabled,
    queryFn: () => api('memberSignInSettings'),
    retry: (count, err) => !(isApiError(err) && err.status < 500) && count < 2,
    staleTime: 0,
  })
